import _ from 'lodash';
import tinycolor from 'tinycolor2';
import * as Colors from 'assets/js/colors';
import { urls } from 'assets/js/api';

const state = {
  primaryColor: Colors.PrimaryColor,
  secondaryColor: Colors.SecondaryColor,
  accentColor: Colors.AccentColor,
  backgroundColor: Colors.BackgroundColor,
  activeLinkColor: Colors.ActiveLinkColor,
  textColor: Colors.TextColor,
  themeId: null,
  name: null,
  logoFullHeight: null,
  logoThumbHeight: null,
  logoFull: null,
  logoThumb: null,
};

const actions = {
  // SetTheme sets the site-wide theme if the theme argument is given, or
  // otherwise resets the theme to the QReserve default.
  SetTheme({ commit }, theme) {
    if (theme) {
      commit('SET_THEME', theme);
    } else {
      commit('RESET_THEME');
    }
  },
};

const mutations = {
  // RESET_THEME resets the theme state to its initial values, representing the
  // default app theming.
  RESET_THEME(stateArg) {
    const stateCopy = stateArg;
    stateCopy.primaryColor = Colors.PrimaryColor;
    stateCopy.secondaryColor = Colors.SecondaryColor;
    stateCopy.accentColor = Colors.AccentColor;
    stateCopy.backgroundColor = Colors.BackgroundColor;
    stateCopy.logoFullHeight = null;
    stateCopy.logoThumbHeight = null;
    stateCopy.themeId = null;
    stateCopy.name = null;
    stateCopy.logoFull = null;
    stateCopy.logoThumb = null;
    stateCopy.css = null;
  },

  // SET_THEME sets the theme state to the values corresponding to a new theme,
  // enabling whitelabelling.
  SET_THEME(stateArg, theme) {
    const stateCopy = stateArg;
    // Use camelCase for theme taken from client.
    stateCopy.primaryColor = theme.primary_color || theme.primaryColor;
    stateCopy.secondaryColor = theme.secondary_color || theme.secondaryColor;
    stateCopy.accentColor = theme.accent_color || theme.accentColor;
    stateCopy.backgroundColor = theme.background_color || theme.backGroundColor;
    stateCopy.textColor = theme.text_color || theme.textColor;
    stateCopy.activeLinkColor =
      theme.active_link_color || theme.activeLinkColor;
    stateCopy.themeId = theme.theme_id || theme.themeId;
    stateCopy.name = theme.name;
    stateCopy.logoFullHeight = theme.logo_full_height;
    stateCopy.logoThumbHeight = theme.logo_thumb_height;
    stateCopy.logoFull = theme.image_full;
    stateCopy.logoThumb = theme.image_thumb;
    stateCopy.css = theme.css ?? '';
  },
};

const getters = {
  // These getters are the various tints of the four theme colours (primary,
  // secondary, accent, and background) that the app uses throughout.

  accentColorDarken10: (stateArg) => tinycolor(stateArg.accentColor)
    .darken(10)
    .toHexString(),

  accentColorTransparent: (stateArg) => tinycolor(stateArg.accentColor)
    .setAlpha(0.5)
    .toRgbString(),

  greyColor: (stateArg) => '#CCCCCC',

  greyColorLighten3Pale: (stateArg, gettersArg) =>
    tinycolor(gettersArg.greyColor)
      .lighten(3)
      .desaturate(15)
      .toHexString(),

  greyColorLighten5: (stateArg, gettersArg) => tinycolor(gettersArg.greyColor)
    .lighten(5)
    .toHexString(),

  helpTextColor: (stateArg) => tinycolor(stateArg.textColor)
    .lighten(10)
    .desaturate(20)
    .toHexString(),

  primaryColorDarken5: (stateArg) => tinycolor(stateArg.primaryColor)
    .darken(5)
    .toHexString(),

  primaryColorDarken10: (stateArg) => tinycolor(stateArg.primaryColor)
    .darken(10)
    .toHexString(),

  primaryColorDarken15: (stateArg) => tinycolor(stateArg.primaryColor)
    .darken(15)
    .toHexString(),

  primaryColorLighten5: (stateArg) => tinycolor(stateArg.primaryColor)
    .lighten(5)
    .toHexString(),

  primaryColorLighten10: (stateArg) => tinycolor(stateArg.primaryColor)
    .lighten(10)
    .toHexString(),

  primaryColorLighten40: (stateArg) => tinycolor(stateArg.primaryColor)
    .lighten(40)
    .toHexString(),

  primaryColorLighten50: (stateArg) => tinycolor(stateArg.primaryColor)
    .lighten(50)
    .toHexString(),

  primaryColorLighten60: (stateArg) => tinycolor(stateArg.primaryColor)
    .lighten(60)
    .toHexString(),

  primaryColorLighten70: (stateArg) => tinycolor(stateArg.primaryColor)
    .lighten(70)
    .toHexString(),

  primaryColorLighten77: (stateArg) => tinycolor(stateArg.primaryColor)
    .lighten(77)
    .toHexString(),

  primaryColorLighten15Pale: (stateArg) => tinycolor(stateArg.primaryColor)
    .lighten(15)
    .desaturate(60)
    .toHexString(),

  primaryColorLighten70Pale: (stateArg) => tinycolor(stateArg.primaryColor)
    .lighten(70)
    .desaturate(54)
    .toHexString(),

  primaryColorLighten77Pale: (stateArg) => tinycolor(stateArg.primaryColor)
    .lighten(76)
    .desaturate(50)
    .toHexString(),

  secondaryColorLighten4: (stateArg) => tinycolor(stateArg.secondaryColor)
    .lighten(4)
    .toHexString(),

  secondaryColorLighten8Pale: (stateArg) => tinycolor(stateArg.secondaryColor)
    .lighten(8)
    .desaturate(40)
    .toHexString(),

  secondaryColorLighten15: (stateArg) => tinycolor(stateArg.secondaryColor)
    .lighten(15)
    .toHexString(),

  secondaryColorLighten25Transparent: (stateArg) =>
    tinycolor(stateArg.secondaryColor)
      .lighten(25)
      .setAlpha(0.25)
      .toRgbString(),

  secondaryColorLighten30: (stateArg) => tinycolor(stateArg.secondaryColor)
    .lighten(30)
    .toHexString(),

  secondaryColorLighten35: (stateArg) => tinycolor(stateArg.secondaryColor)
    .lighten(35)
    .toHexString(),

  secondaryColorLighten40: (stateArg) => tinycolor(stateArg.secondaryColor)
    .lighten(40)
    .toHexString(),

  secondaryColorLighten50: (stateArg) => tinycolor(stateArg.secondaryColor)
    .lighten(50)
    .toHexString(),

  // themeStyle assembles all the css variables from the state and getters into
  // an object which will be assigned to the html element of the app, allowing
  // all elements to have access to them.
  themeStyle: (stateArg, gettersArg) =>
    Object({
      '--accent-color': stateArg.accentColor,
      '--accent-color-darken-10': gettersArg.accentColorDarken10,
      '--accent-color-transparent': gettersArg.accentColorTransparent,
      '--active-link-color': stateArg.activeLinkColor,
      '--background-color': stateArg.backgroundColor,
      '--grey-color': gettersArg.greyColor,
      '--grey-color-lighten-3-pale': gettersArg.greyColorLighten3Pale,
      '--grey-color-lighten-5': gettersArg.greyColorLighten5,
      '--help-text-color': gettersArg.helpTextColor,
      '--text-color': stateArg.textColor,
      '--primary-color': stateArg.primaryColor,
      '--primary-color-darken-5': gettersArg.primaryColorDarken5,
      '--primary-color-darken-10': gettersArg.primaryColorDarken10,
      '--primary-color-darken-15': gettersArg.primaryColorDarken15,
      '--primary-color-lighten-5': gettersArg.primaryColorLighten5,
      '--primary-color-lighten-10': gettersArg.primaryColorLighten10,
      '--primary-color-lighten-15-pale': gettersArg.primaryColorLighten15Pale,
      '--primary-color-lighten-40': gettersArg.primaryColorLighten40,
      '--primary-color-lighten-50': gettersArg.primaryColorLighten50,
      '--primary-color-lighten-60': gettersArg.primaryColorLighten60,
      '--primary-color-lighten-70': gettersArg.primaryColorLighten70,
      '--primary-color-lighten-70-pale': gettersArg.primaryColorLighten70Pale,
      '--primary-color-lighten-77': gettersArg.primaryColorLighten77,
      '--primary-color-lighten-77-pale': gettersArg.primaryColorLighten77Pale,
      '--secondary-color': stateArg.secondaryColor,
      '--secondary-color-lighten-4': gettersArg.secondaryColorLighten4,
      '--secondary-color-lighten-8-pale': gettersArg.secondaryColorLighten8Pale,
      '--secondary-color-lighten-15': gettersArg.secondaryColorLighten15,
      '--secondary-color-lighten-25-transparent':
        gettersArg.secondaryColorLighten25Transparent,
      '--secondary-color-lighten-30': gettersArg.secondaryColorLighten30,
      '--secondary-color-lighten-35': gettersArg.secondaryColorLighten35,
      '--secondary-color-lighten-40': gettersArg.secondaryColorLighten40,
      '--secondary-color-lighten-50': gettersArg.secondaryColorLighten50,
      '--toast-color': _.isNull(stateArg.themeId) ?
        gettersArg.primaryColorLighten10 : gettersArg.primaryColor,
    }),

  // logoLoginUrl returns the url for the logo used in the login screen, if a
  // theme is active, otherwise null.
  logoLoginUrl: (stateArg) => {
    if (stateArg.themeId) return stateArg.logoFull;
    return null;
  },

  // logoSidebarUrl returns the url for the logo used in the app sidebar/header,
  // if a theme is active, otherwise null.
  logoSidebarUrl: (stateArg) => {
    if (stateArg.themeId) return stateArg.logoThumb;
    return null;
  },

  // logoLoginHeight returns the sidebar logo height if provided.
  logoLoginHeight: (stateArg) => {
    if (stateArg.themeId) return stateArg.logoFullHeight;
    return null;
  },

  // logoSidebarHeight returns the sidebar logo height if provided.
  logoSidebarHeight: (stateArg) => {
    if (stateArg.themeId) return stateArg.logoThumbHeight;
    return null;
  },

  themeCss: (stateArg) => {
    if (stateArg.themeId) return stateArg.css ?? '';
    return '';
  },
};

export default {
  actions,
  getters,
  mutations,
  state,
};
